import { useTranslation } from 'react-i18next';
import { Container, Box, Typography, List, ListItem, ListItemText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Section, AnimatedBox } from '../../components/ui';

const PageHeader = styled(Box)(({ theme }) => ({
  background: theme.palette.background.hero,
  padding: theme.spacing(16, 0, 8),
  position: 'relative',

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(12, 0, 6),
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),

  '& h2': {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },

  '& p': {
    marginBottom: theme.spacing(2),
    lineHeight: 1.8,
    color: theme.palette.text.secondary,
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: '12px',

  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600,
  },
}));

export default function Cookies() {
  const { t } = useTranslation();
  const lastUpdated = 'January 2024';

  const cookieTypes = [
    {
      name: 'Essential Cookies',
      purpose: 'Required for the website to function properly',
      duration: 'Session / 1 year',
      examples: 'Session ID, CSRF token',
    },
    {
      name: 'Analytics Cookies',
      purpose: 'Help us understand how visitors interact with our website',
      duration: '2 years',
      examples: 'Google Analytics (_ga, _gid)',
    },
    {
      name: 'Functional Cookies',
      purpose: 'Remember your preferences and settings',
      duration: '1 year',
      examples: 'Language preference, theme settings',
    },
    {
      name: 'Marketing Cookies',
      purpose: 'Track visitors across websites for advertising purposes',
      duration: '90 days - 2 years',
      examples: 'LinkedIn Insight, Facebook Pixel',
    },
  ];

  return (
    <>
      <PageHeader>
        <Container maxWidth="lg">
          <AnimatedBox animation="fadeInUp">
            <Typography variant="h1" sx={{ color: 'text.light', mb: 2 }}>
              Cookie Policy
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Last updated: {lastUpdated}
            </Typography>
          </AnimatedBox>
        </Container>
      </PageHeader>

      <Section>
        <Container maxWidth="md">
          <AnimatedBox animation="fadeInUp">
            <ContentSection>
              <Typography variant="h2" component="h2">1. What Are Cookies</Typography>
              <Typography component="p">
                Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
              </Typography>
              <Typography component="p">
                Cookies allow a website to recognize your device and remember information about your visit, like your preferred language and other settings. This can make your next visit easier and the site more useful to you.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">2. How We Use Cookies</Typography>
              <Typography component="p">
                Nexfah uses cookies and similar tracking technologies to:
              </Typography>
              <List>
                <ListItem><ListItemText primary="Ensure the website functions properly" /></ListItem>
                <ListItem><ListItemText primary="Remember your preferences and settings" /></ListItem>
                <ListItem><ListItemText primary="Analyze how you use our website to improve our services" /></ListItem>
                <ListItem><ListItemText primary="Deliver relevant content and advertisements" /></ListItem>
                <ListItem><ListItemText primary="Protect against fraud and enhance security" /></ListItem>
              </List>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">3. Types of Cookies We Use</Typography>
              <StyledTableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Cookie Type</TableCell>
                      <TableCell>Purpose</TableCell>
                      <TableCell>Duration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cookieTypes.map((cookie) => (
                      <TableRow key={cookie.name}>
                        <TableCell><strong>{cookie.name}</strong></TableCell>
                        <TableCell>{cookie.purpose}</TableCell>
                        <TableCell>{cookie.duration}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">4. Essential Cookies</Typography>
              <Typography component="p">
                These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in, or filling in forms.
              </Typography>
              <Typography component="p">
                You can set your browser to block or alert you about these cookies, but some parts of the site will not then work.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">5. Analytics Cookies</Typography>
              <Typography component="p">
                These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us know which pages are the most and least popular and see how visitors move around the site.
              </Typography>
              <Typography component="p">
                All information these cookies collect is aggregated and therefore anonymous. If you do not allow these cookies, we will not know when you have visited our site.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">6. Third-Party Cookies</Typography>
              <Typography component="p">
                Some cookies are placed by third-party services that appear on our pages. We use the following third-party services:
              </Typography>
              <List>
                <ListItem><ListItemText primary="Google Analytics - For website analytics and performance monitoring" /></ListItem>
                <ListItem><ListItemText primary="LinkedIn - For professional networking features and analytics" /></ListItem>
                <ListItem><ListItemText primary="Facebook/Meta - For social media integration" /></ListItem>
              </List>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">7. Managing Cookies</Typography>
              <Typography component="p">
                You can control and manage cookies in various ways. Most browsers allow you to:
              </Typography>
              <List>
                <ListItem><ListItemText primary="View what cookies are stored and delete them individually" /></ListItem>
                <ListItem><ListItemText primary="Block third-party cookies" /></ListItem>
                <ListItem><ListItemText primary="Block all cookies from specific sites" /></ListItem>
                <ListItem><ListItemText primary="Block all cookies from being set" /></ListItem>
                <ListItem><ListItemText primary="Delete all cookies when you close your browser" /></ListItem>
              </List>
              <Typography component="p">
                Please note that if you choose to block or delete cookies, some features of our website may not function properly.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">8. Browser Settings</Typography>
              <Typography component="p">
                You can manage cookie preferences through your browser settings. Here are links to cookie management instructions for popular browsers:
              </Typography>
              <List>
                <ListItem><ListItemText primary="Google Chrome: Settings → Privacy and security → Cookies" /></ListItem>
                <ListItem><ListItemText primary="Mozilla Firefox: Options → Privacy & Security → Cookies" /></ListItem>
                <ListItem><ListItemText primary="Safari: Preferences → Privacy → Cookies" /></ListItem>
                <ListItem><ListItemText primary="Microsoft Edge: Settings → Cookies and site permissions" /></ListItem>
              </List>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">9. Updates to This Policy</Typography>
              <Typography component="p">
                We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our data practices. When we make changes, we will revise the "Last updated" date at the top of this policy.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">10. Contact Us</Typography>
              <Typography component="p">
                If you have any questions about our use of cookies, please contact us at:
              </Typography>
              <Typography component="p">
                <strong>Nexfah</strong><br />
                Email: info@nexfah.com<br />
                Phone: +44 7888 601455<br />
                London, United Kingdom
              </Typography>
            </ContentSection>
          </AnimatedBox>
        </Container>
      </Section>
    </>
  );
}
